// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";

// Components
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { CheckCircleIcon, PlusIcon } from "src/assets/icons";

import { DEFAULT_EXAM_CARDS, examCardEnums } from "src/constants/enums";

// Contexts
import { useExamSliderContext } from "src/contexts/exams/ExamSliderContext";
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Types
import { SelectedExamCardType } from "src/services/exams/types";

// Styles
import { ExamSliderButton } from "src/styles/mui/muiStylesHelper";

type SelectVideoButtonType = {
  label: string;
  videoId: string;
};

const SelectVideoButton = ({ label, videoId }: SelectVideoButtonType) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { clinicInfo } = useClinicContext();
  const { selectedExamCards, actions } = useExamSliderContext();
  const { setSelectedExamCards } = actions;

  const canUseExamPackaging = clinicInfo?.licensing?.canUseExamPackaging;
  const maxCardArrayLength = canUseExamPackaging ? 5 : 1;

  // A video can only be added once
  const selectedCard = useMemo(
    () => selectedExamCards.find((card) => card.videoId === videoId),
    [selectedExamCards, videoId]
  );

  const isSelected = Boolean(selectedCard);

  const isDisabledButton = useMemo(
    () => selectedExamCards.length >= maxCardArrayLength || isSelected,
    [selectedExamCards, maxCardArrayLength, isSelected]
  );

  const onSelectVideo = useCallback(() => {
    setSelectedExamCards((prev: Array<SelectedExamCardType>) => {
      const defaultCard = DEFAULT_EXAM_CARDS.find(
        (card) => card.examFieldType === examCardEnums.VIDEO.algorithmPath
      );

      return [
        ...prev,
        {
          ...defaultCard,
          videoId,
          videoName: label,
          examNumLabel: 1,
          groupId: selectedExamCards?.length,
        },
      ];
    });
  }, [selectedExamCards, videoId, label, setSelectedExamCards]);

  return (
    <Button
      data-testid={`${videoId}-select-video-button`}
      disabled={isDisabledButton}
      startIcon={
        isSelected ? (
          <Box color="#22BE39">
            <CheckCircleIcon width="16px" height="16px" />
          </Box>
        ) : (
          <PlusIcon width="16px" height="16px" />
        )
      }
      onClick={onSelectVideo}
      sx={ExamSliderButton(theme, isSelected)}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          minWidth: 0,
          gap: "8px",
        }}
      >
        <Typography
          fontSize="12px"
          noWrap
          sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {t(label)}
        </Typography>
        <Typography fontSize="12px" sx={{ flexShrink: 0 }}>
          {isSelected ? `${t("Qty")}: 1` : ""}
        </Typography>
      </Box>
    </Button>
  );
};

export default SelectVideoButton;
